import type { ReactNode } from "react"
import type { OrderDetail } from "../../services/use-orders"
import type { ExchangeRates } from "../../lib/money"
import { humanizeMarketplaceId } from "../../lib/marketplace"
import { formatDateTime } from "../../lib/format-date"
import { MarketplaceMark } from "../marketplaces/marketplace-mark"
import { CopyValue } from "../ui/copy-value"
import { Money } from "../ui/money"
import { preferredTrackingUrl } from "./order-detail-model"
import { OrderExternalLink } from "./order-external-link"

export function OrderSummary({
  data,
  rates,
}: {
  data: OrderDetail
  rates: ExchangeRates | undefined
}) {
  const tracking = data.marketplaceTracking?.trackingCode ?? data.oms.trackingCode
  const trackingUrl = preferredTrackingUrl(data)
  const units = data.lines.reduce((sum, line) => sum + line.qty, 0)
  return (
    <section
      aria-label="Order summary"
      className="divide-y divide-border rounded-xl border border-border bg-card"
    >
      <div className="flex items-center gap-3 p-4 sm:p-5">
        <MarketplaceMark marketplace={data.marketplace} className="size-10" />

        <div className="min-w-0">
          <h2 className="text-base font-semibold">{humanizeMarketplaceId(data.marketplace)}</h2>

          <p className="mt-1 text-xs text-muted-foreground">{data.platformLabel} marketplace</p>
        </div>
      </div>

      <div className="p-4 sm:p-5">
        <p className="text-xs text-muted-foreground">Order value</p>

        <Money
          amount={data.totalPrice}
          currency={data.currency}
          rates={rates}
          layout="stacked"
          className="mt-2 block text-3xl font-semibold tracking-tight"
        />

        <p className="mt-2 text-xs text-muted-foreground">
          {`${units} units · ${data.lineCount} lines`}
        </p>
      </div>

      <dl className="space-y-4 p-4 sm:p-5">
        <Fact label="Customer">
          {data.customerEmail ? <CopyValue value={data.customerEmail} /> : "Not provided"}
        </Fact>

        <Fact label="Accepted on marketplace">
          {data.acceptedAt ? formatDateTime(data.acceptedAt) : "Waiting for acceptance"}
        </Fact>

        <Fact label="Parcel">
          {data.marketplaceTracking?.carrier ? (
            <span className="mr-2">{data.marketplaceTracking.carrier}</span>
          ) : null}

          {tracking ? (
            <CopyValue value={tracking} className="font-mono text-xs" />
          ) : (
            "No tracking number yet"
          )}

          {trackingUrl ? (
            <div className="mt-1">
              <OrderExternalLink url={trackingUrl} label="Track parcel" />
            </div>
          ) : null}
        </Fact>

        <Fact label="Last updated">{formatDateTime(data.lastUpdatedAt)}</Fact>
      </dl>
    </section>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>

      <dd className="mt-1 break-words text-sm tabular-nums">{children}</dd>
    </div>
  )
}
